import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { SimpleGrid } from "@mantine/core";
import { useAtomValue } from "jotai";

import {
  AsyncState,
  BusinessUnitCard,
  PageHeader,
  RegisterCard,
} from "@prostocheck/controls-web";
import { useCashRegisters, useSession } from "@prostocheck/data-web";
import {
  apiErrorAtom,
  apiLoadingAtom,
  cashRegistersAtom,
  groupRegistersByBusinessUnit,
} from "@prostocheck/shared";

import { DASHBOARD_PREVIEW_COUNT } from "./dashboard.constants";
import { DashboardSection } from "./parts";

export const DashboardPage = () => {
  const { t } = useTranslation();
  const { session } = useSession();

  const { getCashRegisters } = useCashRegisters();
  const fetchedRegisters = useAtomValue(cashRegistersAtom);
  const isLoading = useAtomValue(apiLoadingAtom);
  const errorResult = useAtomValue(apiErrorAtom);
  const registers = fetchedRegisters.length > 0 ? fetchedRegisters : (session?.registers ?? []);
  const businessUnits = groupRegistersByBusinessUnit(registers);

  useEffect(() => {
    void getCashRegisters();
  }, [getCashRegisters]);

  return (
    <>
      <PageHeader
        title={t("dashboard.title")}
        description={t("dashboard.description")}
      />

      {errorResult && !isLoading && (
        <div className="mb-6">
          <AsyncState
            isLoading={false}
            errorResult={errorResult}
            onRetry={getCashRegisters}
          />
        </div>
      )}

      <div className="space-y-10">
        <DashboardSection
          title={t("dashboard.businessUnits.title", {
            count: businessUnits.length,
          })}
          viewAllLabel={t("dashboard.businessUnits.viewAll")}
          viewAllTo="/business-units"
        >
          <SimpleGrid cols={{ base: 1, md: 2, lg: 3 }} spacing="md">
            {businessUnits.slice(0, DASHBOARD_PREVIEW_COUNT).map(businessUnit => (
              <BusinessUnitCard
                key={businessUnit.taxObjectId}
                businessUnit={businessUnit}
              />
            ))}
          </SimpleGrid>
        </DashboardSection>

        <DashboardSection
          title={t("dashboard.registers.title", { count: registers.length })}
          viewAllLabel={t("dashboard.registers.viewAll")}
          viewAllTo="/registers"
        >
          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
            {registers.slice(0, DASHBOARD_PREVIEW_COUNT).map(register => (
              <RegisterCard
                key={register.fiscalNumber}
                register={register}
                isLoading={isLoading}
              />
            ))}
          </SimpleGrid>
        </DashboardSection>
      </div>
    </>
  );
};
